import {
  use,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useDeferredValue,
  useRef,
  startTransition,
} from "react";
import { isCancelledError } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  Text,
  Button,
  ProgressCircle,
  SegmentedControl,
  SegmentedControlItem,
  Picker,
  PickerItem,
  mergeStyles,
} from "@react-spectrum/s2";
import FeedbackIcon from "@react-spectrum/s2/icons/Feedback";
import { parseColor } from "react-stately";

import {
  HeaderTitle,
  HeaderContent,
  HeaderActions,
} from "@app/components/Header";
import { UserFeedback } from "@app/components/UserFeedback";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import { VoiceoverStep } from "./new/VoiceoverStep";
import { ScenesStep } from "./new/ScenesStep";
import { TemplateStep } from "./new/TemplateStep";
import { ModularTemplateStep } from "./new/ModularTemplateStep";
import { CustomerDetailsStep } from "./new/CustomerDetailsStep";
import { ScriptStep } from "./new/ScriptStep";
import { CampaignVisualsStep } from "./new/CampaignVisualsStep";
import { BrandingStep } from "./new/BrandingStep";
import { AudioStep } from "./new/AudioStep";
import { ImagesVariationsStep } from "./new/ImagesVariationsStep";
import { BannersStep } from "./new/BannersStep";
import { GenerateVideoStep } from "./new/GenerateVideoStep";
import { FigmaHandoffStep } from "./new/FigmaHandoffStep";
import { DevToolbarProvider } from "./DevToolbar";
import { ProjectMenu } from "./ProjectMenu";

import {
  useVideoWizard,
  useCustomerCheck,
  SELECTION_TYPE,
} from "@app/hooks/videoWizard";
import { useSceneGeneration } from "@app/hooks/sceneGeneration";
import { useVideoFeedback } from "@app/hooks/feedback";
import {
  WIZARD_STEP,
  PRE_FLOW_STEPS,
  resolveSteps,
  getTemplateSteps,
  getContinueLabel,
  getStepEnterAction,
  getStepType,
  STEP_TYPE,
  getImagesSteps,
  hasBannersStep,
  isStepAfter,
} from "./wizardSteps";

import { Step } from "./new/Step";

import { useDownloadAllAssets } from "@app/hooks/videos";
import { usePresignURL } from "@app/hooks/s3";
import { useAudioMux } from "@app/hooks/textToSpeech";
import {
  useWizardStepActions,
  supportsAudioMuxDownloadTemplate,
} from "@app/hooks/useWizardStepActions";
import { VideoWizardContext } from "./videoWizardContext";

import { getImageBlobDimensions } from "@app/utils/imageBlob";
import { getImageBlob, downloadFile } from "@app/services/downloadFile";

import { store } from "@app/store";
import { WizardDemoBoundary } from "./DemoVideoWizard";

import {
  style,
  color,
  size,
  css,
} from "@react-spectrum/s2/style" with { type: "macro" };

const getAssetsFingerprint = (templateAssets, imageVariations, assets) =>
  [
    ...(templateAssets ?? []).map((asset) => asset.name),
    ...(imageVariations ?? []).map((variation) => variation.key),
  ]
    .map((key) => {
      const asset = assets?.find((a) => a.name === key);
      return `${key}:${asset?.filePath ?? ""}`;
    })
    .sort()
    .join("|");

const getExternalHref = (url) =>
  typeof url === "string" && url.trim() ? url.trim() : null;

// Maps a step id to the component that renders it. The two pre-flow entry
// steps (template selection + the account-details dialog) share TemplateStep.
const DEFAULT_STEP_COMPONENTS = {
  [WIZARD_STEP.SELECT_TEMPLATE]: TemplateStep,
  [WIZARD_STEP.ACCOUNT_DETAILS]: TemplateStep,
  [WIZARD_STEP.CUSTOMER_DETAILS]: CustomerDetailsStep,
  [WIZARD_STEP.CAMPAIGN]: ScriptStep,
  [WIZARD_STEP.CAMPAIGN_VISUALS]: CampaignVisualsStep,
  [WIZARD_STEP.SCENES]: ScenesStep,
  [WIZARD_STEP.VOICEOVER]: VoiceoverStep,
  [WIZARD_STEP.BRANDING]: BrandingStep,
  [WIZARD_STEP.AUDIO]: AudioStep,
  [WIZARD_STEP.BANNERS]: BannersStep,
  [WIZARD_STEP.GENERATE_VIDEO]: GenerateVideoStep,
  [WIZARD_STEP.FIGMA_HANDOFF]: FigmaHandoffStep,
};

const MODULAR_STEP_COMPONENTS = {
  [WIZARD_STEP.SELECT_TEMPLATE]: ModularTemplateStep,
  [WIZARD_STEP.ACCOUNT_DETAILS]: ModularTemplateStep,
  [WIZARD_STEP.CUSTOMER_DETAILS]: CustomerDetailsStep,
  [WIZARD_STEP.CAMPAIGN]: ScriptStep,
  [WIZARD_STEP.CAMPAIGN_VISUALS]: CampaignVisualsStep,
  [WIZARD_STEP.SCENES]: ScenesStep,
  [WIZARD_STEP.VOICEOVER]: VoiceoverStep,
  [WIZARD_STEP.BRANDING]: BrandingStep,
  [WIZARD_STEP.AUDIO]: AudioStep,
  [WIZARD_STEP.BANNERS]: BannersStep,
  [WIZARD_STEP.GENERATE_VIDEO]: GenerateVideoStep,
};

const STEP_COMPONENTS_CONFIG = {
  modular: MODULAR_STEP_COMPONENTS,
};

// Steps that opt into a family through their `type`, whatever their id.
const STEP_TYPE_COMPONENTS = {
  [STEP_TYPE.CAMPAIGN_VISUALS]: CampaignVisualsStep,
  [STEP_TYPE.IMAGES]: ImagesVariationsStep,
};

const getTemplateStepComponents = (templateId) =>
  STEP_COMPONENTS_CONFIG[templateId] ?? DEFAULT_STEP_COMPONENTS;

export const VideoWizard = (props) => (
  <WizardDemoBoundary isWizard>
    <EditableVideoWizard {...props} />
  </WizardDemoBoundary>
);

const EditableVideoWizard = ({
  type,
  selectedTemplateKey: defaultSelectedTemplateKey = null,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeStep = params?.step;

  const contentContainerRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const audioMuxRanForSourcesKeyRef = useRef(null);
  const prevAudioMuxDownloadSourcesKeyRef = useRef(null);
  const bannersAssetsFingerprintRef = useRef(null);
  const runStepEnteringActionRef = useRef(null);
  const runStepLeavingActionRef = useRef(null);
  const [projectId, setProjectId] = useState(params?.id);

  const [customerName, setCustomerName] = useState("");
  const deferredCustomerName = useDeferredValue(customerName);
  const [hint, setHint] = useState("");
  const [product, setProduct] = useState("");
  const [customerWebsiteURL, setCustomerWebsiteURL] = useState("");
  const [customerBrandCenterURL, setCustomerBrandCenterURL] = useState("");
  const [customerColor, setCustomerColor] = useState("");
  const [industry, setIndustry] = useState("");
  // Seed the template + account details from the URL query params so a flow
  // started elsewhere (e.g. the home page) can be resumed / deep-linked. The
  // explicit prop still wins when provided.
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(
    defaultSelectedTemplateKey ?? searchParams.get("template") ?? null,
  );
  const [sfType, setSfType] = useState(
    searchParams.get("sfType") ?? "accountName",
  );
  const [drNumbersFilter, setDrNumbersFilter] = useState("");
  const [drNumber, setDrNumber] = useState(searchParams.get("drNumber") ?? "");
  const [accountName, setAccountName] = useState(
    searchParams.get("accountName") ?? "",
  );
  const [campaignView, setCampaignView] = useState("preview");

  const [selectedMusicTrackId, setSelectedMusicTrackId] = useState(null);
  const [demoPilotProjectId, setDemoPilotProjectId] = useState(null);
  const [audioMuxDownloadUrl, setAudioMuxDownloadUrl] = useState(null);
  const [audioMuxDownloadPending, setAudioMuxDownloadPending] = useState(false);

  const colorScheme = useStore(store, (state) => state.colorScheme);
  const isLargeViewport = useStore(store, (state) => state.isLargeViewport);
  const isMobile = useStore(store, (state) => state.isMobile);
  const setPromptHint = useStore(store, (state) => state.setPromptHint);

  const audioMux = useAudioMux();

  const {
    currentStep,
    setCurrentStep,
    video,
    createVideo,
    createVideoIfNeeded,
    updateVideo,
    updateCustomer,
    updateAssets,
    uploadAssets,
    templates,
    template,
    assetsTemplate,
    drNumbers,
    accounts,
    personas,
    selectedPersona,
    setSelectedPersona,
    verticals,
    selectedVertical,
    setSelectedVertical,
    include3DDigitalTwin,
    setInclude3DDigitalTwin,
    customerBranding,
    addCampaign,
    editCampaign,
    scrapeQueries,
    searchImagesQueries,
    appStoreQuery,
    productSearchQuery,
    // taglines,
    bannersStatus,
    generateBanners,
    exportBanners,
    generateVideo,
    exportVideo,
    renderProgress,
    generateStatusText,
    describeMogrtTemplate,
  } = useVideoWizard({
    videoId: projectId,
    routeStep,
    customerName: deferredCustomerName,
    hint,
    product,
    templateType: type,
    templateId: selectedTemplateKey,
    sfType,
    drNumbersFilter,
    drNumber,
    accountName,
    demoPilotProjectId,
    onCreateProject: (data) => {
      navigate(`./${data.id}`);
    },
    onSuccess: (data) => {
      navigate(`../${data.id}`);
    },
  });

  // Destructured out of the mutation objects above (rather than referenced as
  // e.g. `updateAssets.mutate` at the call site) so they're stable identifiers
  const { mutate: updateAssetsMutate, mutateAsync: updateAssetsMutateAsync } =
    updateAssets;
  const { mutateAsync: uploadAssetsMutateAsync } = uploadAssets;
  const { mutate: editCampaignMutate } = editCampaign;
  const { mutate: updateCustomerMutate } = updateCustomer;
  const { mutate: updateVideoMutate } = updateVideo;
  const { mutate: generateVideoMutate } = generateVideo;

  useBreadcrumbs([
    { label: "Projects", href: "/projects" },
    {
      label:
        video.data?.name ??
        (type === "modular" ? "Video Composer" : "New project"),
      isLoading: projectId ? !video.data?.name : false,
    },
  ]);

  const [_agreeToTerms, _setAgreeToTerms] = useState(null);
  const agreeToTerms =
    _agreeToTerms ?? video.data?.agreeToFFUsageTerms ?? false;

  const setAgreeToTerms = (value) => {
    _setAgreeToTerms(value);
    updateVideoMutate({ agreeToFFUsageTerms: value });
  };

  const s3VideoURL = usePresignURL(video.data?.videoPath);
  const selectedMusicTrackURL = usePresignURL(
    video.data?.musicPath ?? selectedMusicTrackId,
  );

  const checkCustomer = useCustomerCheck();

  const sceneGeneration = useSceneGeneration({ videoId: projectId });

  const feedback = useVideoFeedback();

  // The template driving the current flow — used to resolve per-template step
  // configuration (flow + entering/leaving actions).
  const activeTemplateId = template.data?.id ?? selectedTemplateKey;

  // Keep the URL's `:step` segment in sync with the current step. Only active
  // once the project id is in the URL (`/projects/new/:id/:step`); before that the
  // pre-flow steps run at `/projects/new` without a step segment. This also serves
  // the "no `:step` in the path → redirect to the current step" case, since the
  // current step differs from the (undefined) route step.
  useEffect(() => {
    if (
      !activeTemplateId ||
      !params?.id ||
      !currentStep ||
      currentStep === routeStep
    ) {
      return;
    }
    const stepEntry = getTemplateSteps(activeTemplateId).find(
      (s) => s.id === currentStep,
    );
    if (stepEntry?.viewless) {
      return;
    }
    navigate(`/projects/new/${params.id}/${stepEntry?.id ?? currentStep}`, {
      replace: true,
    });
  }, [params?.id, currentStep, routeStep, navigate, activeTemplateId]);

  useEffect(() => {
    if (template.data?.musicTracks?.length > 0) {
      const defaultTrackId = template.data?.musicTracks[0].id;
      setSelectedMusicTrackId(defaultTrackId);

      if (video.data?.id && video.data?.musicPath == null) {
        updateVideoMutate({ musicPath: defaultTrackId });
      }
    }
  }, [
    template.data?.musicTracks,
    video.data?.id,
    video.data?.musicPath,
    updateVideoMutate,
  ]);

  const groupedMusicTracks = useMemo(() => {
    const tracks = template.data?.musicTracks ?? [];
    const groups = [];
    const groupsBySection = new Map();

    for (const track of tracks) {
      const section = track.section ?? "";
      let group = groupsBySection.get(section);
      if (!group) {
        const label = section
          ? section.replace(/\b\w/g, (c) => c.toUpperCase())
          : "Other";
        group = { section, label, items: [] };
        groupsBySection.set(section, group);
        groups.push(group);
      }
      group.items.push(track);
    }

    return groups;
  }, [template.data?.musicTracks]);

  const handleDrNumbersSelectionChange = useCallback(
    (key) => {
      const keyItem = drNumbers.data?.find((item) => item.id === key);

      setDrNumber(key?.drNumber ?? key);

      if (keyItem?.name) {
        setDrNumbersFilter(keyItem.name);
      }
    },
    [drNumbers.data],
  );

  const [editedCampaign, setEditedCampaign] = useState(null);

  const selectedCampaign =
    template.data?.campaignSelectionType !== SELECTION_TYPE.MULTIPLE
      ? video.data?.campaigns?.find(
          (item) => item.id === video.data?.selectedCampaignIds?.[0],
        )
      : video.data?.selectedCampaignIds?.map((id) =>
          video.data?.campaigns?.find((item) => item.id === id),
        );

  const allScrapedQueries = useMemo(
    () =>
      [
        productSearchQuery,
        ...scrapeQueries,
        ...searchImagesQueries,
        appStoreQuery,
      ].filter((q) => q.isEnabled),
    [productSearchQuery, scrapeQueries, searchImagesQueries, appStoreQuery],
  );

  const scrapedColors = useMemo(
    () =>
      Array.from(
        new Set(
          ([
            !!video.data?.customer?.color
              ? parseColor(video.data?.customer?.color)
              : null,
          ],
          scrapeQueries
            .flatMap((query) => query.data || [])
            ?.filter((asset) => asset.type === "color")
            // ?.sort((a, b) => (a.method === "ai" ? -1 : 1))
            // ?.map((asset) => ({
            //   colors: asset.colors.map(({ r, g, b }) =>
            //     parseColor(`rgb(${r},${g},${b})`).toString("hex"),
            //   ),
            // }))
            .flatMap(({ color }) => color?.toUpperCase()) || [])
            .flat()
            .filter(Boolean),
        ),
      ),
    [video.data?.customer?.color, scrapeQueries],
  );

  const selectedAssets =
    template.data?.assets?.filter((asset) =>
      video.data?.assets?.some((a) => a.name === asset.name),
    ) ?? [];

  const areAllVisualsSelected = useMemo(() => {
    const brandingAssets = template.data?.assets?.filter(
      (asset) =>
        (asset.type === "image" || asset.type === "color") &&
        // Optional assets never block progressing to the next step.
        !asset.isOptional,
    );
    if (brandingAssets == null) {
      return false;
    }
    return brandingAssets.every((asset) =>
      video.data?.assets?.some((a) => a.name === asset.name && !a.isInvalid),
    );
  }, [template.data?.assets, video.data?.assets]);

  const assetsFingerprint = useMemo(
    () =>
      getAssetsFingerprint(
        template.data?.assets,
        template.data?.imageVariations,
        video.data?.assets,
      ),
    [template.data?.assets, template.data?.imageVariations, video.data?.assets],
  );

  const [editVariation, setEditVariation] = useState(null);

  const [taglinesData, setTaglinesData] = useState(new Array(3).fill({}));

  const handleTaglineChange = useCallback((index, key, value) => {
    setTaglinesData((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [key]: value } : item,
      ),
    );
  }, []);

  // After refresh, resume mogrt job polling via generateVideo (poll-only path in render hook)
  useEffect(() => {
    const jobId = video.data?.jobId;
    const videoPath = video.data?.videoPath;

    if (
      videoPath ||
      generateVideo.isPending ||
      !projectId ||
      video.data?.templateType !== "mogrt" ||
      !video.isSuccess ||
      !jobId
    ) {
      return;
    }

    generateVideoMutate(
      { videoId: projectId, resumeMogrtJob: true },
      {
        onError: (error) => {
          if (
            isCancelledError(error) ||
            error?.cause?.problem === "CANCEL_ERROR"
          ) {
            return;
          }
          updateVideoMutate({
            status: null,
            jobId: null,
          });

          setCurrentStep(
            hasBannersStep(template.data)
              ? WIZARD_STEP.BANNERS
              : (getImagesSteps(activeTemplateId, template.data).at(-1)?.id ??
                  WIZARD_STEP.BRANDING),
          );
        },
      },
    );

    return;
  }, [
    projectId,
    activeTemplateId,
    video.data?.videoPath,
    video.data?.templateType,
    video.data?.jobId,
    video.isSuccess,
    template.data,
    generateVideo.isPending,
    updateVideoMutate,
    generateVideoMutate,
    setCurrentStep,
  ]);

  // Redirect to video view if video already has videoPath (completed)
  // useEffect(() => {
  //   if (params?.id && video.isSuccess && video.data?.videoPath) {
  //     navigate(`/projects/${params.id}`, { replace: true });
  //   }
  // }, [
  //   params?.id,
  //   video.isSuccess,
  //   video.data?.videoPath,
  //   location.pathname,
  //   navigate,
  // ]);

  // Initialize taglines data with selected campaign taglines
  useEffect(() => {
    if (
      !!selectedCampaign?.taglines?.length &&
      taglinesData.every((tagline) => !Object.keys(tagline).length)
    ) {
      setTaglinesData(selectedCampaign?.taglines ?? new Array(3).fill({}));
    }
  }, [taglinesData, selectedCampaign?.taglines]);

  const handleUploadAssets = useCallback(
    (items, fileName) =>
      new Promise((resolve, reject) => {
        const getFileName = (name) =>
          name
            ? `upload__${name.substr(0, 50)}__${crypto.randomUUID()}`
            : `upload__${crypto.randomUUID()}`;

        const getAssetTypeFromBlob = (blob, originalName = "") => {
          const mime = blob?.type ?? "";
          if (mime.startsWith("video/")) {
            return "video";
          }
          if (
            !mime &&
            typeof originalName === "string" &&
            /\.(mp4|webm|mov|m4v|mkv|avi)(\?|$)/i.test(originalName)
          ) {
            return "video";
          }
          return "image";
        };

        startTransition(async () => {
          const assets = await Promise.all(
            Array.from(items).map(async (item) => {
              if (item instanceof File) {
                const type = getAssetTypeFromBlob(item, item.name);
                return {
                  name: getFileName(item.name),
                  blob: item,
                  ...(type ? { type } : {}),
                };
              } else if (typeof item.getFile === "function") {
                const file = await item.getFile();
                const type = getAssetTypeFromBlob(file, file.name);
                return {
                  name: getFileName(file.name),
                  blob: file,
                  ...(type ? { type } : {}),
                };
              } else if (
                item?.kind === "text" &&
                item.types.has("image/scrapped")
              ) {
                const text = await item.getText("image/scrapped");
                const content = JSON.parse(text);
                const imageBlob = await getImageBlob({ url: content.src });

                return {
                  name: getFileName(imageBlob.image.name),
                  blob: imageBlob.blob,
                };
              } else if (item?.src) {
                const imageBlob = await getImageBlob({ url: item.src });

                return {
                  name: getFileName(imageBlob.image.name),
                  blob: imageBlob.blob,
                };
              }
            }),
          );

          await Promise.all(
            assets.filter(Boolean).map(async (asset) => {
              if (asset.type === "video") {
                return asset;
              }

              try {
                const dimensions = await getImageBlobDimensions(asset.blob);
                asset.size = dimensions;

                return asset;
              } catch (e) {
                console.warn(
                  "Failed to get image dimensions for asset",
                  asset,
                  e,
                );

                return asset;
              }
            }),
          );

          if (
            typeof fileName === "string" &&
            Array.from(items).some((item) => item?.types?.has("image/asset"))
          ) {
            try {
              const [item] = Array.from(items);
              const text = await item.getText("image/asset");
              const { presignURL, ...asset } = JSON.parse(text);

              await updateAssetsMutateAsync({
                assets: [{ ...asset, name: fileName }],
              });

              resolve({
                assets: [asset],
                fileName,
              });
            } catch (e) {
              console.error("Failed to parse asset from dropped item", e);

              reject(new Error("Failed to parse asset from dropped item"));
            }
          } else {
            const updatedVideoData = await uploadAssetsMutateAsync(
              {
                assets,
                fileName,
              },
              // {
              //   onSuccess: (data) => {
              //     // Reference the uploaded asset with a specific fileName
              //     if (typeof fileName === "string" && assets.length === 1) {
              //       const uploadedAsset = data?.assets.find(
              //         (d) => d.name === assets[0].name,
              //       );

              //       if (uploadedAsset) {
              //         updateAssets.mutate({
              //           assets: [{ ...uploadedAsset, name: fileName }],
              //         });
              //       }
              //     }
              //   },
              // },
            );

            const uploadedAssetNames = assets.map((asset) => asset.name);

            resolve({
              assets:
                updatedVideoData?.assets?.filter((a) =>
                  uploadedAssetNames.includes(a.name),
                ) ?? assets,
              fileName,
            });
          }

          resolve({
            assets,
          });
        });
      }),
    [uploadAssetsMutateAsync, updateAssetsMutateAsync],
  );

  const handleColorChange = useCallback(
    ({ color, name }) => {
      if (!color || !name) {
        return;
      }

      const hex = color.toString("hex");

      updateAssetsMutate(
        {
          assets: [
            {
              name,
              type: "color",
              color: hex,
            },
          ],
        },
        {
          onSuccess: (data, variables) => {
            const oldColor = selectedCampaign?.[name];
            const newColor = variables.assets.find(
              (a) => a.name === name,
            )?.color;

            if (oldColor === newColor) {
              return;
            }

            const scenes = (selectedCampaign?.scenes ?? []).map((scene) => {
              const updatedScene = { ...scene };

              if (scene?.Color?.includes(oldColor) && !!newColor) {
                updatedScene.Color = scene.Color.map((c) =>
                  c === oldColor ? newColor : c,
                );
              }

              return updatedScene;
            });

            editCampaignMutate(
              {
                id: selectedCampaign?.id,
                [name]: hex,
                scenes,
              },
              {
                onSuccess: () => {
                  // TODO: remove after refactoring
                  updateCustomerMutate({
                    customer: {
                      [name]: hex,
                      [name === "Brand-color" ? "color" : "secondaryColor"]:
                        hex,
                    },
                  });
                },
              },
            );
          },
        },
      );
    },
    [
      updateAssetsMutate,
      updateCustomerMutate,
      editCampaignMutate,
      selectedCampaign,
    ],
  );

  const handleAssetMenuAction = useCallback(
    (action, asset) => {
      if (action === "delete") {
        updateAssetsMutate({ toRemove: [asset] });
      } else {
        if (asset?.filePath) {
          updateAssetsMutate({ assets: [{ ...asset, name: action }] });
        } else {
          handleUploadAssets([asset], action);
        }
      }
    },
    [updateAssetsMutate, handleUploadAssets],
  );

  const [statusTextIndex, setStatusTextIndex] = useState(0);

  // Cycle through status text while generating video or banners
  useEffect(() => {
    let intervalId = null;

    if (generateVideo.isPending || exportVideo.isFetching) {
      intervalId = setInterval(() => {
        setStatusTextIndex(
          (prevIndex) => (prevIndex + 1) % generateStatusText.length,
        );
      }, 5000);
    } else {
      setStatusTextIndex(0);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    generateVideo.isPending,
    exportVideo.isFetching,
    generateStatusText.length,
  ]);

  const handleNextStep = useCallback(
    (step) => {
      const fromStep = currentStep;

      setCurrentStep(step);

      document
        .getElementById("beStory-content")
        ?.scrollTo({ top: 0, behavior: "smooth" });

      // Run the step-we're-leaving's background action (e.g. voiceover mux).
      if (fromStep && fromStep !== step) {
        runStepLeavingActionRef.current?.(fromStep);
      }

      // Run the entered step's action (create video, generate scenes/banners,
      // start the render, …) — all configured per step in `STEP_ACTIONS`.
      runStepEnteringActionRef.current?.(step);
    },
    [currentStep, setCurrentStep],
  );

  const handleTemplateContinue = useCallback(() => {
    handleNextStep(WIZARD_STEP.ACCOUNT_DETAILS);
  }, [handleNextStep]);

  const handleTemplateSelectionChange = useCallback(
    (keys) => {
      const [key] = keys;
      setSelectedTemplateKey(key);

      handleTemplateContinue();
    },
    [handleTemplateContinue],
  );

  const handleLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRegenerateScript = (options = {}) => {
    updateVideo.mutate(
      {
        customer: {
          name: video.data?.customer?.name,
        },
        assets: [],
        campaigns: [],
        selectedCampaignIds: [],
      },
      options,
    );
  };

  const isCustomerDetailsDisabled =
    !customerName ||
    (activeTemplateId === "firefly-enterprise" && updateVideo.isPending) ||
    (sfType === "drNumber" && !drNumber) ||
    (sfType === "accountName" && !accountName);

  const downloadAllAssets = useDownloadAllAssets(projectId);

  useEffect(() => {
    bannersAssetsFingerprintRef.current = null;
  }, [projectId]);

  // Baseline variation assets for an existing project that already has banners
  useEffect(() => {
    if (
      !video.isSuccess ||
      !video.data?.templateAssetsRenditions?.length ||
      bannersAssetsFingerprintRef.current !== null
    ) {
      return;
    }

    bannersAssetsFingerprintRef.current = assetsFingerprint;
  }, [
    video.isSuccess,
    video.data?.templateAssetsRenditions,
    assetsFingerprint,
  ]);

  // Remember variation assets after banners are successfully exported
  useEffect(() => {
    if (
      currentStep !== WIZARD_STEP.BANNERS ||
      !exportBanners.isSuccess ||
      exportBanners.isFetching ||
      generateBanners.isPending
    ) {
      return;
    }

    bannersAssetsFingerprintRef.current = assetsFingerprint;
  }, [
    currentStep,
    exportBanners.isSuccess,
    exportBanners.isFetching,
    generateBanners.isPending,
    assetsFingerprint,
  ]);

  // Set customer details
  useEffect(() => {
    if (video.isEnabled && video.data?.customer) {
      setCustomerName(video.data?.customer?.name || "");
      setIndustry(video.data?.customer?.industry || "");
      setCustomerWebsiteURL(video.data?.customer?.websiteURL || "");
      setCustomerBrandCenterURL(video.data?.customer?.brandCenterURL || "");
      setCustomerColor(video.data?.customer?.color || "");
    }
  }, [video.isEnabled, video.data?.customer]);

  // Set project details
  useEffect(() => {
    if (video.isEnabled && video.isSuccess) {
      const _sfType = !!video.data?.drNumber
        ? "drNumber"
        : !!video.data?.accountName
          ? "accountName"
          : "none";

      if (video.data?.templateId !== selectedTemplateKey) {
        setSelectedTemplateKey(video.data?.templateId);
      }
      if (video.data?.accountName !== accountName) {
        setAccountName(video.data?.accountName || "");
      }
      if (video.data?.drNumber !== drNumber) {
        setDrNumber(video.data?.drNumber || "");
      }
      if (_sfType !== sfType) {
        setSfType(_sfType);
      }
      if (video.data?.hint != null && video.data.hint !== hint) {
        setHint(video.data.hint);
      }
      if (video.data?.product != null && video.data.product !== product) {
        setProduct(video.data.product);
      }
      if (
        video.data?.musicPath != null &&
        video.data.musicPath !== selectedMusicTrackId
      ) {
        setSelectedMusicTrackId(video.data.musicPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.isEnabled, video.isSuccess]);

  // Generate banners when entering the step if not generated yet — handled by
  // the BANNERS entering action (`runBannersTransition`) via `handleNextStep`,
  // and by the unified missing-data placeholder after a refresh. No effect.

  useEffect(() => {
    setAudioMuxDownloadUrl(null);
    prevAudioMuxDownloadSourcesKeyRef.current = null;
  }, [projectId]);

  const handleDownloadAudioMux = useCallback(async () => {
    if (!audioMuxDownloadUrl) {
      return;
    }
    setAudioMuxDownloadPending(true);
    try {
      const contentType = "audio/wav";
      const { blob } = await getImageBlob({
        url: audioMuxDownloadUrl,
        contentType,
      });
      downloadFile(blob, `${projectId}-mux-audio`, contentType);
    } catch (e) {
      console.error("Audio mux download failed:", e);
    } finally {
      setAudioMuxDownloadPending(false);
    }
  }, [audioMuxDownloadUrl, projectId]);

  const activeSteps = useMemo(
    () => resolveSteps(activeTemplateId, template.data),
    [activeTemplateId, template.data],
  );

  // While a (re)generation is in flight, the previous run's video must not show.
  // `generateVideo` stays pending until its chained export resolves.
  const downloadVideoUrl = generateVideo.isPending
    ? null
    : getExternalHref(
        s3VideoURL.data?.presignURL || exportVideo.data?.presignURL,
      );

  const isVideoRendered =
    !generateVideo.isPending && !!(downloadVideoUrl || exportVideo.isSuccess);

  return (
    <VideoWizardContext.Provider
      value={{
        // identifiers & refs
        videoId: projectId,
        templateId: selectedTemplateKey,
        actionsContainerRef,
        contentContainerRef,

        // wizard state / navigation
        currentStep,
        setCurrentStep,
        handleNextStep,
        activeSteps,
        activeTemplateId,
        setSelectedTemplateKey,

        // step action dispatch refs (assigned by useWizardStepActions)
        runStepEnteringActionRef,
        runStepLeavingActionRef,

        // core queries & mutations (from useVideoWizard)
        video,
        template,
        templates,
        assetsTemplate,
        createVideo,
        createVideoIfNeeded,
        updateVideo,
        updateCustomer,
        updateAssets,
        uploadAssets,
        addCampaign,
        editCampaign,
        generateScenes: sceneGeneration.mutation,
        regenerateScenes: sceneGeneration.regenerate,
        getScenesPromptTemplate: sceneGeneration.getScenesPromptTemplate,
        customerBranding,
        drNumbers,
        accounts,
        personas,
        selectedPersona,
        setSelectedPersona,
        verticals,
        selectedVertical,
        setSelectedVertical,
        scrapeQueries,
        searchImagesQueries,
        appStoreQuery,
        bannersStatus,
        generateBanners,
        exportBanners,
        generateVideo,
        exportVideo,
        renderProgress,
        generateStatusText,
        describeMogrtTemplate,
        checkCustomer,
        audioMux,

        // wizard-input state + setters
        customerName,
        setCustomerName,
        hint,
        setHint,
        product,
        setProduct,
        include3DDigitalTwin,
        setInclude3DDigitalTwin,
        sfType,
        setSfType,
        drNumber,
        drNumbersFilter,
        setDrNumbersFilter,
        accountName,
        setAccountName,
        selectedTemplateKey,
        handleTemplateSelectionChange,
        setProjectId,

        // step-local UI state owned by the parent (referenced by effects/handlers)
        agreeToTerms,
        setAgreeToTerms,
        editedCampaign,
        setEditedCampaign,
        editVariation,
        setEditVariation,
        taglinesData,
        handleTaglineChange,
        selectedMusicTrackId,
        setSelectedMusicTrackId,
        selectedMusicTrackURL,
        groupedMusicTracks,
        statusTextIndex,
        audioMuxDownloadUrl,
        audioMuxDownloadPending,
        setAudioMuxDownloadUrl,
        prevAudioMuxDownloadSourcesKeyRef,

        // shared handlers
        handleUploadAssets,
        handleColorChange,
        handleAssetMenuAction,
        handleDrNumbersSelectionChange,
        handleTemplateContinue,
        handleRegenerateScript,
        handleDownloadAudioMux,
        handleLink,

        // derived values
        selectedCampaign,
        selectedAssets,
        scrapedColors,
        allScrapedQueries,
        areAllVisualsSelected,
        isCustomerDetailsDisabled,
        downloadAllAssets,
        downloadVideoUrl,
        isVideoRendered,
        assetsFingerprint,

        // refs for step action hook
        bannersAssetsFingerprintRef,
        audioMuxRanForSourcesKeyRef,

        // misc app state
        colorScheme,
        isLargeViewport,
        isMobile,
        setPromptHint,
        feedback,
      }}
    >
      <title>{`${!!video.data?.name ? `${video.data?.name} | ` : ""}New video | BeStory`}</title>
      <VideoWizardContent type={type} />
    </VideoWizardContext.Provider>
  );
};

VideoWizard.propTypes = {
  type: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  // Optional preselected template. Seeds `selectedTemplateKey` so the wizard can
  // be started from outside (e.g. the home page) skipping template selection.
  selectedTemplateKey: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Inner component — renders inside the provider so it can consume context.
// Calls useWizardStepActions (which reads from VideoWizardContext) and
// overrides context with the computed step-action values.
// ---------------------------------------------------------------------------

const VideoWizardContent = ({ type }) => {
  const ctx = use(VideoWizardContext);
  const {
    stepActionCtx,
    regenerateBannersFromCurrentAssets,
    computeVoiceoverMuxKey,
  } = useWizardStepActions();

  const {
    currentStep,
    handleNextStep,
    activeSteps,
    activeTemplateId,
    template,
    video,
    isLargeViewport,
    isMobile,
    feedback,
    contentContainerRef,
    actionsContainerRef,
    setAudioMuxDownloadUrl,
    prevAudioMuxDownloadSourcesKeyRef,
    runStepEnteringActionRef,
  } = ctx;

  const stepperSteps = useMemo(
    () =>
      activeSteps.filter(
        (step) => !PRE_FLOW_STEPS.includes(step.id) && !step.viewless,
      ),
    [activeSteps],
  );

  const currentIndex = activeSteps.findIndex((step) => step.id === currentStep);
  const currentStepEntry = activeSteps[currentIndex];
  const nextStep =
    currentIndex >= 0 ? activeSteps[currentIndex + 1] : undefined;

  let prevStep;
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (!activeSteps[i].viewless) {
      prevStep = activeSteps[i];
      break;
    }
  }

  const enterAction = getStepEnterAction(
    currentStepEntry ?? currentStep,
    activeTemplateId,
  );

  const isStepPending = enterAction?.isPending(stepActionCtx);

  const showStepPlaceholder =
    !!enterAction?.placeholder &&
    !enterAction.isReady(stepActionCtx) &&
    !isStepPending;

  const isViewlessStep = !!currentStepEntry?.viewless;

  const isViewlessStepReady =
    isViewlessStep && !!enterAction?.isReady(stepActionCtx) && !!nextStep;

  useEffect(() => {
    if (isViewlessStepReady) {
      handleNextStep(nextStep.id);
    }
  }, [isViewlessStepReady, nextStep?.id, handleNextStep]);

  // Invalidate the audio-mux download URL when the voiceover source changes.
  const audioMuxSourcePathsKey = useMemo(
    () => computeVoiceoverMuxKey(video.data),
    [computeVoiceoverMuxKey, video.data],
  );

  useEffect(() => {
    if (!supportsAudioMuxDownloadTemplate(template.data?.id)) {
      setAudioMuxDownloadUrl(null);
      prevAudioMuxDownloadSourcesKeyRef.current = null;
      return;
    }
    if (!audioMuxSourcePathsKey) {
      return;
    }
    if (prevAudioMuxDownloadSourcesKeyRef.current !== audioMuxSourcePathsKey) {
      setAudioMuxDownloadUrl(null);
      prevAudioMuxDownloadSourcesKeyRef.current = audioMuxSourcePathsKey;
    }
  }, [template.data?.id, audioMuxSourcePathsKey]);

  if (currentStep === null) {
    return (
      <div
        className={style({
          size: "full",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <ProgressCircle
          aria-label="Loading"
          isIndeterminate
          size="L"
          staticColor="auto"
        />
      </div>
    );
  }

  const goNext = nextStep ? () => handleNextStep(nextStep.id) : undefined;
  const goBack =
    prevStep && !PRE_FLOW_STEPS.includes(prevStep.id)
      ? () => handleNextStep(prevStep.id)
      : undefined;
  const continueLabel = getContinueLabel(nextStep);
  const ActiveStepComponent =
    STEP_TYPE_COMPONENTS[getStepType(currentStepEntry ?? currentStep)] ??
    getTemplateStepComponents(type)[currentStep];

  const shouldShowWizardSteps =
    isStepAfter(currentStepEntry ?? currentStep, WIZARD_STEP.ACCOUNT_DETAILS) &&
    !isViewlessStep;

  const WizardSteps = (props) =>
    shouldShowWizardSteps ? (
      <SegmentedControl
        selectedKey={currentStep}
        onSelectionChange={handleNextStep}
        {...props}
      >
        {stepperSteps.map((step, idx) => (
          <SegmentedControlItem
            key={step.id}
            id={step.id}
            isDisabled={
              video.data?.status === "completed"
                ? false
                : activeSteps.findIndex((s) => s.id === step.id) > currentIndex
            }
          >
            <Text styles={style({ display: "inline-flex", gap: 8 })}>
              <Text>{String.fromCodePoint(10112 + idx)}</Text>
              <Text>{step.name}</Text>
            </Text>
          </SegmentedControlItem>
        ))}
      </SegmentedControl>
    ) : null;

  const MobileWizardSteps = (props) =>
    shouldShowWizardSteps ? (
      <Picker
        selectionMode="single"
        value={currentStep}
        onChange={handleNextStep}
        {...props}
      >
        {stepperSteps.map((step, idx) => (
          <PickerItem
            key={step.id}
            id={step.id}
            isDisabled={
              video.data?.status === "completed"
                ? false
                : activeSteps.findIndex((s) => s.id === step.id) > currentIndex
            }
          >
            <Text>{`${String.fromCodePoint(10112 + idx)}\xA0\xA0${step.name}`}</Text>
          </PickerItem>
        ))}
      </Picker>
    ) : null;

  return (
    <VideoWizardContext.Provider
      value={{
        ...ctx,
        isStepPending,
        stepActionCtx,
        regenerateBannersFromCurrentAssets,
      }}
    >
      <div
        ref={contentContainerRef}
        className={style({
          boxSizing: "border-box",
          display: "grid",
          gridTemplateAreas: [
            "steps steps steps steps",
            ". . . .",
            "content content content sidebar",
            ". . . .",
            "info info info info",
            "footer . actions actions",
          ],
          gridTemplateColumns: [
            "minmax(0, 1fr)",
            48,
            "max-content",
            "minmax(0, auto)",
          ],
          gridTemplateRows: ["auto", 32, "1fr", 48, "auto", "auto"],
          width: "full",
          // minHeight: "full",
          paddingX: { md: 24 },
        })}
      >
        <DevToolbarProvider>
          <HeaderTitle>
            <ProjectMenu item={video.data} />
          </HeaderTitle>

          <HeaderContent>
            {isLargeViewport && (
              <WizardSteps
                styles={style({ marginX: "auto", gridArea: "content" })}
              />
            )}
          </HeaderContent>

          <HeaderActions>
            <Button
              variant="primary"
              onPress={feedback.open}
              styles={style({
                gridArea: "actions",
                marginStart: {
                  default: "auto",
                  isLargeViewport: {
                    shouldShowWizardSteps: "0",
                    default: "auto",
                  },
                },
              })({ isLargeViewport, shouldShowWizardSteps })}
            >
              <FeedbackIcon />
              <Text>Share feedback</Text>
            </Button>
          </HeaderActions>

          <UserFeedback
            video={video.data}
            isOpen={feedback.isOpen}
            onClose={feedback.close}
            method={feedback.method}
          />

          {!isLargeViewport && !isMobile && (
            <WizardSteps
              UNSAFE_className={style({
                gridArea: "steps",
                marginX: "auto",
                boxShadow: "elevated",
                position: "sticky",
                top: 24,
                zIndex: 10,
              })}
              UNSAFE_style={{
                backgroundColor: color("transparent-overlay-100"),
                backdropFilter: `blur(${size(4)})`,
              }}
            />
          )}

          {isMobile && (
            <MobileWizardSteps
              styles={style({
                gridArea: "steps",
                width: "full",
              })}
            />
          )}

          {isViewlessStep ? (
            showStepPlaceholder ? (
              <>
                <Step>
                  <Step.Error
                    title={enterAction.placeholder.title}
                    retryLabel={enterAction.placeholder.action}
                    onRetry={() =>
                      runStepEnteringActionRef.current?.(currentStep)
                    }
                  >
                    {enterAction.placeholder.body}
                  </Step.Error>
                </Step>
                <Step.Actions onBack={goBack} />
              </>
            ) : (
              <Step.Loading
                title={enterAction?.pending?.title}
                subtitle={enterAction?.pending?.subtitle}
                icon={enterAction?.pending?.icon}
              />
            )
          ) : (
            ActiveStepComponent &&
            (showStepPlaceholder ? (
              <>
                <Step>
                  <Step.Error
                    title={enterAction.placeholder.title}
                    retryLabel={enterAction.placeholder.action}
                    onRetry={() =>
                      runStepEnteringActionRef.current?.(currentStep)
                    }
                  >
                    {enterAction.placeholder.body}
                  </Step.Error>
                </Step>
                <Step.Actions onBack={goBack} />
              </>
            ) : (
              <ActiveStepComponent
                name={currentStepEntry?.name}
                description={currentStepEntry?.description}
                preview={currentStepEntry?.preview}
                contentLabel={currentStepEntry?.contentLabel}
                canEditCampaign={currentStepEntry?.canEditCampaign}
                showPromptFooter={currentStepEntry?.showPromptFooter}
                stages={currentStepEntry?.stages}
                campaignStructure={currentStepEntry?.campaignStructure}
                promptHintsKey={currentStepEntry?.promptHintsKey}
                editScope={currentStepEntry?.editScope}
                continueLabel={continueLabel}
                onContinue={goNext}
                onBack={goBack}
              />
            ))
          )}

          <div
            ref={actionsContainerRef}
            className={mergeStyles(
              style({
                gridArea: "actions",
                alignSelf: "end",
                justifySelf: "end",

                padding: 12,
                backgroundColor: "elevated",
                borderRadius: "pill",
                boxShadow: "elevated",

                // marginY: 24,

                position: "sticky",
                bottom: 24,
              }),
              css(`&:empty { display: none; }`),
            )}
          />
        </DevToolbarProvider>
      </div>
    </VideoWizardContext.Provider>
  );
};

VideoWizardContent.propTypes = {
  type: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};
